import { create } from 'zustand';
import type { NovelGame, NovelGenerateInput } from './types';

export type NovelPhase = 'hub' | 'setup' | 'play' | 'error';

interface NovelState {
  phase: NovelPhase;
  draft: NovelGenerateInput;
  game: NovelGame | null;
  sceneId: string | null;
  errorMessage: string | null;
  source: 'llm' | 'fallback' | null;
  openSetup: () => void;
  backToHub: () => void;
  setDraft: (patch: Partial<NovelGenerateInput>) => void;
  startPlay: (game: NovelGame, source: 'llm' | 'fallback') => void;
  choose: (nextScene: string) => void;
  restart: () => void;
  setError: (message: string) => void;
  leaveGames: () => void;
}

const DEFAULT_DRAFT: NovelGenerateInput = {
  genre: 'Horror',
  tone: 'Dark',
  setting: 'Station',
  playTime: 'Short',
};

export const useNovelStore = create<NovelState>((set, get) => ({
  phase: 'hub',
  draft: { ...DEFAULT_DRAFT },
  game: null,
  sceneId: null,
  errorMessage: null,
  source: null,

  openSetup: () => set({ phase: 'setup', errorMessage: null }),

  backToHub: () =>
    set({
      phase: 'hub',
      game: null,
      sceneId: null,
      errorMessage: null,
      source: null,
    }),

  setDraft: (patch) => set({ draft: { ...get().draft, ...patch } }),

  startPlay: (game, source) =>
    set({
      phase: 'play',
      game,
      sceneId: game.startScene,
      source,
      errorMessage: null,
    }),

  choose: (nextScene) => set({ sceneId: nextScene }),

  restart: () => {
    const game = get().game;
    if (!game) return;
    set({ sceneId: game.startScene, phase: 'play', errorMessage: null });
  },

  setError: (message) =>
    set({
      phase: 'error',
      errorMessage: message,
      game: null,
      sceneId: null,
    }),

  leaveGames: () =>
    set({
      phase: 'hub',
      game: null,
      sceneId: null,
      errorMessage: null,
      source: null,
      draft: { ...DEFAULT_DRAFT },
    }),
}));
